<template>
  <div>
    <div v-if="shortDescription" class="d-flex align-center">
      <div class="subtitle2 mr-1" data-testid="ticket-preview-title">
        {{ $t("general.description") }}:
      </div>
      <div class="body2" data-testid="ticket-preview-process-description">
        {{ shortDescription }}
      </div>
    </div>

    <template v-if="edrMetaData?.fullDescription">
      <a v-if="!showFullDescription" class="coro-link body2" @click="handleShowFullDescription">
        {{ $t("general.seeMore") }}
      </a>
      <template v-if="showFullDescription">
        <span class="body2 text-indigo-medium">
          {{ edrMetaData.fullDescription }}
        </span>
        <br />
        <a class="coro-link body2 mt-1" @click="handleHideFullDescription">
          {{ $t("general.seeLess") }}
        </a>
      </template>
    </template>

    <div v-if="edrMetaData?.processName" class="d-flex align-center mb-1 mt-1">
      <div class="subtitle2 mr-1" data-testid="ticket-preview-title">
        {{ $t("ticketDetails.process") }}:
      </div>
      <div v-if="hideEdrLink" class="body2" data-testid="ticket-preview-process-name">
        {{ edrMetaData.processName }}
      </div>
      <div
        v-else
        class="coro-link body2"
        data-testid="ticket-preview-process-name"
        @click="handleEdrProcessLinkClick()"
      >
        {{ edrMetaData.processName }}
      </div>
    </div>

    <div
      v-if="edrMetaData?.allowBlockTimestamp"
      class="body2"
      :class="{
        'text-green-dark': edrMetaData.processStatus === EdrProcessStatus.ALLOWED,
        'text-red-dark': edrMetaData.processStatus === EdrProcessStatus.BLOCKED,
      }"
    >
      {{
        $t(`ticketDetails.${edrMetaData.processStatus}On`, {
          date: getFormattedDateTime(edrMetaData.allowBlockTimestamp, "ll"),
        })
      }}
    </div>

    <div v-if="edrMetaData?.processHash" class="d-flex align-center mb-1">
      <div class="subtitle2 mr-1">{{ $t("ticketDetails.hash") }}:</div>
      <div class="body2" data-testid="ticket-preview-process-hash">
        <ellipsified-copy-text max-width="520px" class="body2" :text="edrMetaData.processHash" />
      </div>
    </div>

    <div v-if="asrDetails?.parentProcessName" class="d-flex align-center mb-1">
      <div class="subtitle2 mr-1">{{ $t("ticketDetails.parentProcessName") }}:</div>
      <div class="body2" data-testid="ticket-preview-parent-process-name">
        {{ asrDetails.parentProcessName }}
      </div>
    </div>

    <div v-if="asrDetails?.parentProcessHash" class="d-flex align-center mb-1">
      <div class="subtitle2 mr-1">{{ $t("ticketDetails.parentHash") }}:</div>
      <div class="body2" data-testid="ticket-preview-parent-process-hash">
        <ellipsified-copy-text
          max-width="520px"
          class="body2"
          :text="asrDetails.parentProcessHash"
        />
      </div>
    </div>

    <div class="d-flex align-center mb-1">
      <div class="subtitle2 mr-1">{{ $t("ticketDetails.affectedDevices") }}:</div>
      <div v-if="hideDeviceTableLink" class="body2" data-testid="ticket-preview-affected-devices">
        {{ edrMetaData?.affectedDevicesCount }}
      </div>
      <span
        v-else
        class="coro-link body2"
        data-testid="ticket-preview-affected-devices"
        @click="handleDeviceLinkClick()"
        >{{ edrMetaData?.affectedDevicesCount }}</span
      >
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref } from "vue";
import { useTicketPreviewGeneralInfo } from "@/composables/useTicketPreviewGeneralInfo";
import { useI18n } from "vue-i18n";
import { Service } from "@/constants/cloud-apps";
import { ProxyDirection } from "@/constants/email-proxy";
import { EdrProcessStatus } from "@/constants/edr";
import EllipsifiedCopyText from "@/components/EllipsifiedCopyText.vue";
import type { TicketDetails } from "@/_store/tickets/tickets.module.ts";

export default defineComponent({
  name: "TicketEdrInfoBaseComponent",
  components: {
    EllipsifiedCopyText,
  },
  props: {
    ticketDetails: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
    hideEdrLink: {
      type: Boolean,
      required: true,
    },
    hideDeviceTableLink: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["device-link-click", "edr-process-link-click"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const showFullDescription = ref(false);

    const edrMetaData = computed(() => props.ticketDetails?.sections?.edrMetaData);

    const asrDetails = computed(() => edrMetaData.value?.asrDetails);

    const shortDescription = computed(() => {
      const reasonKey = `ticketDetails.asrDescriptions.${asrDetails.value?.detectionReason}`;

      return asrDetails.value?.detectionReason && i18n.te(reasonKey)
        ? i18n.t(reasonKey)
        : edrMetaData.value?.shortDescription;
    });

    const { getFormattedDateTime, addUsersNotAllowed, usersToAdd, addUsers, openListModal } =
      useTicketPreviewGeneralInfo(props.ticketDetails);

    const users = computed(() => {
      const lastLoggedInUsers = props.ticketDetails?.lastLoggedInUsers ?? [];
      return lastLoggedInUsers.length
        ? lastLoggedInUsers.join(", <br>")
        : props.ticketDetails.sections.triggeredUser;
    });

    const usersTitle = computed(() => {
      const lastLoggedInUsers = props.ticketDetails?.lastLoggedInUsers?.length;
      return lastLoggedInUsers
        ? i18n.t("ticketDetails.lastUsersOnDevice")
        : i18n.t("ticketDetails.users");
    });

    const serviceNameTitle = computed(() => {
      const serviceName = i18n.t(`services.${props.ticketDetails.sections.serviceName}`);
      if (props.ticketDetails.sections.serviceName === Service.PROXY) {
        const direction =
          props.ticketDetails.sections.emailMetaData?.emailProxyDirection === ProxyDirection.INBOUND
            ? i18n.t("emailProxy.directions.inbound")
            : i18n.t("emailProxy.directions.outbound");

        return `${direction} ${serviceName}`;
      }
      return serviceName;
    });

    const handleShowFullDescription = () => {
      showFullDescription.value = true;
    };

    const handleHideFullDescription = () => {
      showFullDescription.value = false;
    };

    const handleDeviceLinkClick = () => {
      emit("device-link-click");
    };

    const handleEdrProcessLinkClick = () => {
      emit("edr-process-link-click");
    };

    return {
      showFullDescription,
      users,
      getFormattedDateTime,
      addUsersNotAllowed,
      usersToAdd,
      addUsers,
      edrMetaData,
      asrDetails,
      shortDescription,
      serviceNameTitle,
      openListModal,
      Service,
      handleShowFullDescription,
      handleHideFullDescription,
      handleDeviceLinkClick,
      handleEdrProcessLinkClick,
      usersTitle,
      EdrProcessStatus,
    };
  },
});
</script>
